/**
 * CL-PRODUCT-SELECTIONS-006A — Supplier/company linkage for product options (pure).
 *
 * Links to canonical organizations via stable IDs — never duplicates CRM records.
 */

import { isValidProjectProductPathId } from "./projectProductPaths";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function normalizeProductSupplierOrganizationId(value = "") {
  const id = safeStr(value);
  if (!id) return null;
  if (!isValidProjectProductPathId(id)) return null;
  return id;
}

export function buildProductSupplierDisplaySnapshot(organization = {}) {
  const displayName = safeStr(organization.displayName || organization.name);
  if (!displayName) return null;
  return {
    organizationId: safeStr(organization.id) || null,
    displayName,
    organizationType: safeStr(organization.organizationType) || null,
    phone: safeStr(organization.phone) || null,
    website: safeStr(organization.website) || null,
  };
}

export function resolveProductSupplierDisplayName(option = {}, organizationsById = {}) {
  const snapshotName = safeStr(option?.supplierDisplaySnapshot?.displayName);
  if (snapshotName) return snapshotName;
  const orgId = safeStr(option?.supplierOrganizationId || option?.organizationId);
  const org = orgId ? organizationsById[orgId] : null;
  return safeStr(org?.displayName || org?.name) || safeStr(option?.manufacturer) || "";
}

export function validateProductSupplierLink({
  supplierOrganizationId = "",
  organizationsById = {},
} = {}) {
  const orgId = normalizeProductSupplierOrganizationId(supplierOrganizationId);
  if (!orgId) return { ok: true, supplierOrganizationId: null, supplierDisplaySnapshot: null };
  const org = organizationsById[orgId];
  if (!org) return { ok: false, error: "Choose a valid project supplier or company." };
  return {
    ok: true,
    supplierOrganizationId: orgId,
    organizationId: orgId,
    supplierDisplaySnapshot: buildProductSupplierDisplaySnapshot(org),
  };
}

export function listProjectSupplierOrganizationOptions(organizations = [], engagements = []) {
  const activeOrgIds = new Set(
    (Array.isArray(engagements) ? engagements : [])
      .filter((row) => safeStr(row.status).toLowerCase() !== "archived")
      .map((row) => safeStr(row.organizationId))
      .filter(Boolean)
  );
  return (Array.isArray(organizations) ? organizations : [])
    .filter((row) => safeStr(row.status).toLowerCase() !== "archived")
    .filter((row) => activeOrgIds.size === 0 || activeOrgIds.has(safeStr(row.id)))
    .map((row) => ({
      value: safeStr(row.id),
      label: safeStr(row.displayName || row.name) || safeStr(row.id),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function indexProductsBySupplierOrganization(optionsByRequirementId = {}, requirements = []) {
  const byOrgId = new Map();
  (Array.isArray(requirements) ? requirements : []).forEach((requirement) => {
    const requirementId = safeStr(requirement.id);
    const options = optionsByRequirementId[requirementId] || [];
    options.forEach((option) => {
      const orgId = safeStr(option?.supplierOrganizationId || option?.organizationId);
      if (!orgId) return;
      if (!byOrgId.has(orgId)) byOrgId.set(orgId, []);
      byOrgId.get(orgId).push({
        requirementId,
        requirementTitle: safeStr(requirement.title),
        optionId: safeStr(option.id),
        optionLabel: safeStr(option.label),
        decisionStatus: safeStr(option.decisionStatus),
      });
    });
  });
  return byOrgId;
}

export function resolveProjectSupplierShareForOrganization(shares = [], organizationId = "") {
  const orgId = safeStr(organizationId);
  if (!orgId) return null;
  return (Array.isArray(shares) ? shares : []).find((row) => (
    safeStr(row.organizationId) === orgId
    && safeStr(row.status).toLowerCase() !== "archived"
  )) || null;
}

export function resolveProjectSupplierClientShareLabel(share = null) {
  if (!share) return "";
  return share.clientVisible === true ? "Visible" : "Staff only";
}

export function westernGraniteSupplierShareRegressionFixture() {
  return {
    organizationId: "wqvitHWM4Ph6i3oS4Ngf",
    displayName: "Western Granite",
  };
}
